import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { BookOpen, ArrowLeft, Play } from 'lucide-react';
import { API_URL } from '../api-config';
import { handleAuthError } from '../hooks/ValidateToken';

const GenreBooks = () => {
  const { genre } = useParams();
  const [books, setBooks] = useState([]);
  const { usuarioId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchBooksByGenre = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.get(`${API_URL}/books-by-genre/${encodeURIComponent(genre)}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setBooks(response.data || []);
      } catch (err) {
        setError('Error al cargar los libros. Por favor, intenta nuevamente.');
        handleAuthError(err, navigate);
      } finally {
        setLoading(false);
      }
    };

    fetchBooksByGenre();
  }, [genre]);

  const goBack = () => {
    navigate('/search');
  };

  const goToAudioPlayer = (audiolibroId) => {
    navigate(`/reproducir/${usuarioId}/${audiolibroId}?origin=books-by-genre&genre=${encodeURIComponent(genre)}`);
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex flex-col items-center px-4 pt-24"
      role="main"
      aria-label="Libros por género"
    >
      {/* Botón Volver */}
      <button
        onClick={goBack}
        className="fixed top-6 left-6 z-20 bg-purple-600 text-white px-4 py-2 rounded-full hover:bg-purple-700 transition-all duration-300 shadow-lg"
        aria-label="Volver a la página anterior"
      >
        <ArrowLeft className="w-5 h-5 inline-block mr-2" aria-hidden="true" /> Volver
      </button>

      {/* Título principal */}
      <h1 id="genre-books-title" className="text-3xl font-bold text-purple-400 mb-8">
        Libros de {decodeURIComponent(genre)}
      </h1>

      {/* Estado de carga */}
      {loading && (
        <div className="flex flex-col items-center p-12" aria-live="polite">
          <div
            className="w-12 h-12 border-t-4 border-purple-400 rounded-full animate-spin mb-4"
            aria-hidden="true"
          ></div>
          <p className="text-purple-300">Cargando libros...</p>
        </div>
      )}

      {/* Mensaje de error */}
      {error && (
        <div
          className="flex items-center justify-center p-6 bg-red-900/20 rounded-lg border border-red-500/50"
          role="alert"
        >
          <p className="text-red-500">{error}</p>
        </div>
      )}

      {/* Lista de libros */}
      {!loading && books.length > 0 && (
        <div
          className="w-full max-w-5xl mb-20"
          aria-labelledby="genre-books-title"
          role="region"
        >
          <ul className="space-y-4" aria-label="Lista de libros del género">
            {books.map((book) => (
              <li
                key={book.id}
                onClick={() => goToAudioPlayer(book.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    goToAudioPlayer(book.id);
                  }
                }}
                tabIndex="0"
                className="flex justify-between items-center py-4 pl-4 pr-2 bg-slate-800 rounded-lg cursor-pointer hover:bg-slate-700 transition-colors"
                role="button"
                aria-label={`Reproducir audiolibro: ${book.nombre}. Autor: ${book.autor}, Género: ${book.genero}`}
              >
                {/* Contenido del libro */}
                <div className="flex flex-col items-start w-11/12">
                  {/* Nombre */}
                  <h3 className="text-lg font-bold text-white text-justify">{book.nombre}</h3>

                  {/* Autor */}
                  <p className="text-sm text-purple-300">Autor: {book.autor}</p>

                  {/* Género */}
                  <p className="text-sm text-purple-300">Género: {book.genero}</p>

                  {/* Descripción */}
                  <p className="text-sm text-gray-400 mt-2 text-justify">{book.descripcion}</p>
                </div>

                {/* Ícono de reproducción */}
                <div className="flex items-center justify-center md:mr-6 ml-2">
                  <Play
                    size={24}
                    className="text-purple-400 cursor-pointer border-2 border-purple-400 rounded-full p-1 hover:bg-purple-700 hover:text-white transition-all duration-300"
                    onClick={(e) => {
                      e.stopPropagation(); // Evitar que el clic se propague al contenedor
                      goToAudioPlayer(book.id);
                    }}
                    aria-label={`Reproducir audiolibro: ${book.nombre}`}
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Mensaje cuando no hay libros */}
      {!loading && books.length === 0 && (
        <p className="text-purple-300 text-lg" aria-live="polite">
          No se encontraron libros para el género "{decodeURIComponent(genre)}".
        </p>
      )}
    </div>
  );
};

export default GenreBooks;